// Company store editor — /sales/company-stores/new and /:id (S2, CEO
// 2026-10-12; spec docs/company-stores-mvp.md). One page, in the order the rep
// works: client + name → logo → products → payments → ordering rule →
// sign-off → people → orders.
//
// Everything above sign-off is a local draft saved with one Save:
//   new store      POST /company-stores, then PUT …/products
//   existing store PATCH /company-stores/:id (changed fields only), then
//                  PUT …/products when the list changed
// Sign-off (POST …/signoff) and pause / re-open act at once, on the saved
// store, so they ask for unsaved changes to be saved first.
//
// The logo is uploaded through the website's own POST /storefront/logos —
// the API only accepts a store logo uploaded there, because that is where its
// ink colours are counted for pricing. A new logo after sign-off sends the
// store back to the client (S1 updateStore).
//
// Until the company-store tables exist the API answers 503; the page says so
// calmly (database step pending, Jake runs scripts/push-company-stores.ts).
import { DeleteEntityAction } from "@/components/common/DeleteEntityAction";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle2, Copy, ImagePlus, Loader2, Plus, Search, X, XCircle } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { FlashBanner } from "@/components/common/FlashBanner";
import {
  StoreProductsSection,
  draftFromStoreProduct,
  productPayload,
  type DraftProduct,
} from "@/components/company-stores/StoreProductsSection";
import { StoreOrdersSection } from "@/components/company-stores/StoreOrdersSection";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PageHeader } from "@/components/ui/PageHeader";
import { TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { StatusPill } from "@/components/ui/StatusPill";
import { ApiError, api, apiErrorMessage } from "@/lib/api";
import {
  NOT_SET_UP_COPY,
  PAYMENT_LABEL,
  PORTAL_HOST,
  SLUG_RE,
  STATUS_LABEL,
  STATUS_TONE,
  TERMS_LABEL,
  isNotSetUp,
  shortDate,
  slugify,
  type ApprovalRule,
  type InvoiceTerms,
  type PaymentOption,
  type StoreAddress,
  type StoreDetail,
  type StoreMember,
  type UploadedLogo,
} from "@/lib/companyStores";
import { cn } from "@/lib/utils";
import { storeChecklist } from "@/lib/store-checklist";
import { STORE_EDITOR_ROLES } from "@/pages/CompanyStoresListPage";

const PAYMENT_ORDER: PaymentOption[] = ["company_card", "invoice", "employee_card"];
const TERMS_CHOICES: InvoiceTerms[] = ["net_15", "net_30", "net_45", "net_60"];
const LABEL = "mb-1 block text-sm font-semibold text-ink";
const HELP = "mt-1 text-[13px] text-muted";
const SELECT = "h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink disabled:opacity-60";

const errText = (e: unknown, fallback: string) => (isNotSetUp(e) ? NOT_SET_UP_COPY : apiErrorMessage(e, fallback));

interface Draft {
  client: { id: string; name: string } | null;
  name: string;
  slug: string;
  /** False on a new store until the rep edits the link name: it follows the name. */
  slugTouched: boolean;
  logoFileId: string | null;
  paymentOptions: PaymentOption[];
  invoiceTerms: InvoiceTerms;
  approvalRule: ApprovalRule;
  approvalThreshold: string;
  /** Shipping charge by order size, as typed (Stephanie 2026-10-22). */
  shippingRanges: Array<{ from: string; charge: string }>;
  /** End date (Stephanie 2026-10-22): the calendar's YYYY-MM-DD, and "No end date". */
  endsOn: string;
  noEndDate: boolean;
  /** Where orders ship (Stephanie 2026-10-22): each person types their own,
   *  or one of the store's delivery addresses (one = every order ships there). */
  shipMode: "own" | "list";
  addresses: DraftAddress[];
  /** Ships to the store's address → billed one way, to this contact ("" = whoever ordered). */
  billingContactId: string;
  products: DraftProduct[];
}

type DraftAddress = { label: string; company: string; street1: string; street2: string; city: string; stateCode: string; postalCode: string; residential: boolean };
const EMPTY_ADDRESS: DraftAddress = { label: "", company: "", street1: "", street2: "", city: "", stateCode: "", postalCode: "", residential: false };
const draftAddress = (a: StoreAddress): DraftAddress => ({ label: a.label ?? "", company: a.company ?? "", street1: a.street1, street2: a.street2 ?? "", city: a.city, stateCode: a.stateCode, postalCode: a.postalCode, residential: a.residential });

const EMPTY_DRAFT: Draft = {
  client: null,
  name: "",
  slug: "",
  slugTouched: false,
  logoFileId: null,
  paymentOptions: [],
  invoiceTerms: "net_30",
  approvalRule: "none",
  approvalThreshold: "",
  shippingRanges: [],
  endsOn: "",
  noEndDate: true,
  shipMode: "own",
  addresses: [],
  billingContactId: "",
  products: [],
};

function draftFromStore(s: StoreDetail): Draft {
  return {
    client: { id: s.clientId, name: s.clientName ?? "" },
    name: s.name,
    slug: s.slug,
    slugTouched: true,
    logoFileId: s.logoFileId,
    paymentOptions: s.paymentOptions,
    invoiceTerms: s.invoiceTerms ?? "net_30",
    approvalRule: s.approvalRule,
    approvalThreshold: s.approvalThreshold === null ? "" : String(s.approvalThreshold),
    shippingRanges: (s.shippingRanges ?? []).map((r) => ({ from: String(r.from), charge: String(r.charge) })),
    endsOn: s.endsOn ?? "",
    noEndDate: !s.endsOn,
    shipMode: s.shipToList?.length ? "list" : "own",
    addresses: (s.shipToList ?? []).map(draftAddress),
    billingContactId: s.billingContactId ?? "",
    products: s.products.map(draftFromStoreProduct),
  };
}

/** The store fields as the API takes them (one shape for POST and PATCH). */
function fieldsPayload(d: Draft) {
  const paymentOptions = PAYMENT_ORDER.filter((p) => d.paymentOptions.includes(p));
  return {
    clientId: d.client?.id ?? null,
    name: d.name.trim(),
    slug: d.slug.trim(),
    logoFileId: d.logoFileId,
    paymentOptions,
    invoiceTerms: paymentOptions.includes("invoice") ? d.invoiceTerms : null,
    approvalRule: d.approvalRule,
    approvalThreshold: d.approvalRule === "over_amount" && d.approvalThreshold.trim() !== "" ? Number(d.approvalThreshold) : null,
    shippingRanges: d.shippingRanges.map((r) => ({ from: Number(r.from || 0), charge: Number(r.charge || 0) })),
    endsOn: d.noEndDate ? null : d.endsOn || null,
    shipToList: d.shipMode === "list" ? d.addresses.map((a) => ({ ...a, stateCode: a.stateCode.trim().toUpperCase() })) : [],
    billingContactId: d.shipMode === "list" ? d.billingContactId || null : null,
  };
}

/** A plain sentence for what's wrong with the shipping ranges, or null. */
export function shippingRangesProblem(rows: ReadonlyArray<{ from: string; charge: string }>): string | null {
  if (rows.length === 0) return null;
  const nums = rows.map((r) => ({ from: Number(r.from), charge: Number(r.charge) }));
  if (nums.some((r) => !Number.isFinite(r.from) || !Number.isFinite(r.charge) || r.from < 0 || r.charge < 0 || rows.some((x) => x.charge.trim() === ""))) {
    return "Each shipping range needs a shipping charge of $0 or more.";
  }
  if (nums[0]!.from !== 0) return "The first shipping range has to start at $0.";
  for (let i = 1; i < nums.length; i++) {
    if (!(nums[i]!.from > nums[i - 1]!.from)) return "Each shipping range has to start above the one before it.";
  }
  return null;
}

const productsKey = (d: Draft) => JSON.stringify(d.products.map(productPayload));

/** A plain sentence for the first thing stopping a save, or null. */
function problem(d: Draft): string | null {
  if (!d.client) return "Pick the client this store is for.";
  if (!d.name.trim()) return "Give the store a name.";
  const slug = d.slug.trim();
  if (!slug || slug.length > 60 || !SLUG_RE.test(slug)) return "The store link name can only use lower-case letters, numbers and dashes.";
  if (d.approvalRule === "over_amount") {
    const n = Number(d.approvalThreshold);
    if (d.approvalThreshold.trim() === "" || !Number.isFinite(n) || n < 0) return "Enter the order amount above which orders need approval.";
  }
  if (!d.noEndDate && !d.endsOn) return "Pick the store's end date, or tick No end date.";
  if (d.shipMode === "list") {
    if (d.addresses.length === 0) return "Add the address orders ship to, or choose \"Each person enters their own address\".";
    const bad = d.addresses.findIndex((a) => !a.street1.trim() || !a.city.trim() || !/^[A-Za-z]{2}$/.test(a.stateCode.trim()) || !/^\d{5}(-\d{4})?$/.test(a.postalCode.trim()));
    if (bad >= 0) return `Delivery address ${bad + 1} needs a street, city, state and 5-digit ZIP.`;
    if (d.paymentOptions.length !== 1 || d.paymentOptions[0] === "employee_card") return "Pick how this store is billed: Invoice or Company card.";
    if (!d.billingContactId) return "Pick who is billed for this store's orders.";
  }
  const ship = shippingRangesProblem(d.shippingRanges);
  if (ship) return ship;
  if (d.products.some((p) => p.gone)) return "Remove the products that are no longer on the website.";
  return null;
}

export function CompanyStoreEditorPage() {
  const { id } = useParams<{ id: string }>();
  const q = useQuery({
    queryKey: ["company-stores", "detail", id],
    queryFn: () => api<{ store: StoreDetail }>(`/company-stores/${id}`),
    enabled: !!id,
    refetchOnWindowFocus: false,
    retry: (n, e) => !isNotSetUp(e) && !(e instanceof ApiError && e.status === 404) && n < 2,
  });

  if (id && q.isLoading) {
    return <div className="mx-auto mt-4 h-24 max-w-5xl animate-pulse rounded-md bg-white" aria-label="Loading store" />;
  }
  if (id && (q.isError || !q.data)) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <div className="mx-auto max-w-3xl py-16">
        <BackLink />
        {isNotSetUp(q.error) ? (
          <p role="status" className="mt-6 rounded-md border border-rule bg-canvas px-4 py-3 text-sm text-ink">{NOT_SET_UP_COPY}</p>
        ) : (
          <p role="alert" className="mt-6 text-sm text-risk">
            {notFound ? "We couldn't find that store." : errText(q.error, "We couldn't load this store. Refresh to try again.")}
          </p>
        )}
      </div>
    );
  }
  return <StoreEditor key={q.data?.store.id ?? "new"} store={q.data?.store ?? null} />;
}

function BackLink() {
  return (
    <Link to="/sales/company-stores" className="inline-flex items-center gap-1 text-sm text-muted hover:text-ink">
      <ArrowLeft className="h-4 w-4" aria-hidden /> All company stores
    </Link>
  );
}

function StoreEditor({ store }: { store: StoreDetail | null }) {
  const isNew = store === null;
  const { user } = useAuth();
  const canEdit = STORE_EDITOR_ROLES.some((r) => user?.roles.includes(r));
  const navigate = useNavigate();
  const qc = useQueryClient();
  // "New store" from an account's Store tab arrives with the client picked.
  const [params] = useSearchParams();
  const [draft, setDraft] = useState<Draft>(() => {
    if (store) return draftFromStore(store);
    const clientId = params.get("clientId");
    return clientId ? { ...EMPTY_DRAFT, client: { id: clientId, name: params.get("clientName") ?? "" } } : EMPTY_DRAFT;
  });
  const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [uploaded, setUploaded] = useState<{ logo: UploadedLogo; preview: string | null } | null>(null);

  const baseline = useMemo(() => (store ? draftFromStore(store) : EMPTY_DRAFT), [store]);
  const changedFields = useMemo(() => {
    const now = fieldsPayload(draft) as Record<string, unknown>;
    const was = fieldsPayload(baseline) as Record<string, unknown>;
    return Object.keys(now).filter((k) => JSON.stringify(now[k]) !== JSON.stringify(was[k]));
  }, [draft, baseline]);
  const productsChanged = productsKey(draft) !== productsKey(baseline);
  const dirty = isNew ? true : changedFields.length > 0 || productsChanged;

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setName = (name: string) => setDraft((d) => ({ ...d, name, slug: d.slugTouched ? d.slug : slugify(name) }));

  const save = useMutation({
    mutationFn: async (): Promise<{ store: StoreDetail; needsSignoff: boolean; productsError?: string; shippingError?: string }> => {
      const f = fieldsPayload(draft);
      if (isNew) {
        const { slug, invoiceTerms, approvalThreshold, shippingRanges, endsOn, shipToList, billingContactId, ...rest } = f;
        const created = await api<{ store: StoreDetail }>("/company-stores", {
          method: "POST",
          body: JSON.stringify({
            ...rest,
            // Untouched: the API picks a free link name from the store name.
            ...(draft.slugTouched ? { slug } : {}),
            invoiceTerms,
            approvalThreshold,
          }),
        });
        // Shipping ranges + end date are saved on the new store straight after it's made.
        let shippingError: string | undefined;
        if (shippingRanges.length || endsOn || shipToList.length) {
          // Ships to the store's address: one billing method (checked by the API).
          try {
            const later = { ...(shippingRanges.length ? { shippingRanges } : {}), ...(endsOn ? { endsOn } : {}), ...(shipToList.length ? { shipToList, billingContactId } : {}) };
            const r = await api<{ store: StoreDetail }>(`/company-stores/${created.store.id}`, { method: "PATCH", body: JSON.stringify(later) });
            created.store = r.store;
          } catch (e) {
            shippingError = errText(e, "The shipping ranges / end date didn't save.");
          }
        }
        if (!draft.products.length) return { store: created.store, needsSignoff: false, shippingError };
        try {
          const r = await api<{ store: StoreDetail }>(`/company-stores/${created.store.id}/products`, {
            method: "PUT",
            body: JSON.stringify({ products: draft.products.map(productPayload) }),
          });
          return { store: r.store, needsSignoff: false, shippingError };
        } catch (e) {
          return { store: created.store, needsSignoff: false, productsError: errText(e, "The products didn't save."), shippingError };
        }
      }
      let latest = store;
      let needsSignoff = false;
      if (changedFields.length) {
        const patch = Object.fromEntries(changedFields.map((k) => [k, (f as Record<string, unknown>)[k]]));
        const r = await api<{ store: StoreDetail; needsSignoff: boolean }>(`/company-stores/${store.id}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        });
        latest = r.store;
        needsSignoff = r.needsSignoff;
        // The fields are saved even if the products fail below.
        qc.setQueryData(["company-stores", "detail", store.id], { store: r.store });
      }
      if (productsChanged) {
        const r = await api<{ store: StoreDetail }>(`/company-stores/${store.id}/products`, {
          method: "PUT",
          body: JSON.stringify({ products: draft.products.map(productPayload) }),
        });
        latest = r.store;
      }
      return { store: latest, needsSignoff };
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["company-stores", "list"] });
      if (isNew) {
        const flash = r.productsError
          ? `Store created, but the products didn't save (${r.productsError}). Please add them again.`
          : r.shippingError
            ? `Store created, but the shipping ranges / end date didn't save (${r.shippingError}).`
            : "Store created. Next: check everything, then send it to the client to sign off.";
        qc.setQueryData(["company-stores", "detail", r.store.id], { store: r.store });
        navigate(`/sales/company-stores/${r.store.id}`, { replace: true, state: { flash } });
        return;
      }
      qc.setQueryData(["company-stores", "detail", r.store.id], { store: r.store });
      setDraft(draftFromStore(r.store));
      setUploaded(null);
      setMessage({
        kind: "ok",
        text: r.needsSignoff
          ? "Saved. The logo changed, so the client needs to sign off on the store again."
          : "Saved.",
      });
    },
    onError: (e) => setMessage({ kind: "err", text: errText(e, "We couldn't save the store. Please try again.") }),
  });

  const onSave = () => {
    setMessage(null);
    const p = problem(draft);
    if (p) { setMessage({ kind: "err", text: p }); return; }
    save.mutate();
  };

  const locked = !canEdit || save.isPending;
  // The API refuses a client change once the store was sent or approved.
  const clientLocked = !isNew && (store.status !== "draft" || !!store.signedOffAt);

  return (
    <div className="mx-auto max-w-5xl pb-16">
      <BackLink />
      <FlashBanner />
      <PageHeader
        eyebrow={<>Sales / <span>Company stores</span></>}
        title={isNew ? "New store" : store.name}
        intro={
          isNew ? (
            "Pick the client, add their logo and products, choose how people pay, then send it to the client to sign off."
          ) : (
            <span className="flex flex-wrap items-center gap-2">
              <StatusPill tone={STATUS_TONE[store.status]} className="normal-case">{STATUS_LABEL[store.status]}</StatusPill>
              <span>
                for <Link to={`/clients/${store.clientId}`} className="text-ink hover:underline">{store.clientName}</Link>
                {store.repName ? ` · rep ${store.repName}` : ""} · created {shortDate(store.createdAt)}
              </span>
            </span>
          )
        }
        action={
          canEdit ? (
            <Button onClick={onSave} disabled={save.isPending || !dirty}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {save.isPending ? "Saving…" : isNew ? "Create store" : dirty ? "Save changes" : "Saved"}
            </Button>
          ) : undefined
        }
      />

      {message ? <Notice kind={message.kind} text={message.text} onClose={() => setMessage(null)} /> : null}
      {!canEdit ? <p className="mb-6 text-sm text-muted">You can look at this store. Only reps and admins can change it.</p> : null}

      <fieldset disabled={locked} className="min-w-0 space-y-6">
        <Section title="Client and store name">
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <span className={LABEL}>Client</span>
              <ClientPicker value={draft.client} onChange={(c) => set("client", c)} disabled={locked || clientLocked} />
              {clientLocked ? <p className={HELP}>The client can only change while the store is a draft that was never sent.</p> : null}
            </div>
            <label className="block">
              <span className={LABEL}>Store name</span>
              <Input value={draft.name} onChange={(e) => setName(e.target.value)} maxLength={200} placeholder="e.g. Acme team store" />
            </label>
            <label className="block md:col-span-2">
              <span className={LABEL}>Store link name</span>
              <Input
                value={draft.slug}
                onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value.toLowerCase(), slugTouched: true }))}
                maxLength={60}
                placeholder="acme"
                className="md:max-w-sm"
              />
              <p className={HELP}>
                People sign in at {PORTAL_HOST} to reach the store. New people can join at{" "}
                <span className="font-medium text-ink">{PORTAL_HOST}/join/{draft.slug.trim() || "…"}</span>. Lower-case letters, numbers and dashes.
              </p>
            </label>
          </div>
        </Section>

        <Section title="Logo" intro="The logo goes on every product in the store. The client approves it when they sign off.">
          <LogoSection
            store={store}
            logoFileId={draft.logoFileId}
            uploaded={uploaded}
            onUploaded={(logo, preview) => {
              setUploaded((old) => {
                if (old?.preview) URL.revokeObjectURL(old.preview);
                return { logo, preview };
              });
              set("logoFileId", logo.id);
            }}
            onUndo={() => {
              setUploaded((old) => {
                if (old?.preview) URL.revokeObjectURL(old.preview);
                return null;
              });
              set("logoFileId", baseline.logoFileId);
            }}
            disabled={locked}
          />
        </Section>

        <Section title="Products" intro="What people can order. Set the colors they can choose and the order they appear in.">
          <StoreProductsSection
            products={draft.products}
            onChange={(p) => set("products", p)}
            disabled={locked}
            mockupTarget={store && store.logoFileId ? { clientId: store.clientId, clientName: store.clientName ?? "", logoFileId: store.logoFileId } : null}
          />
        </Section>

        {draft.shipMode === "list" ? (
          <Section title="Billing" intro="This store ships to its own address, so you choose once how its orders are billed and who gets the invoice. Employees don't pay at checkout.">
            <StoreBillingEditor draft={draft} setDraft={setDraft} />
          </Section>
        ) : (
        <Section title="How people pay" intro="Choose every option this client allows. People pick one at checkout.">
          <div className="space-y-3">
            {PAYMENT_ORDER.map((p) => (
              <div key={p}>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-ink"
                  checked={draft.paymentOptions.includes(p)}
                  onChange={(e) =>
                    set("paymentOptions", e.target.checked ? [...draft.paymentOptions, p] : draft.paymentOptions.filter((o) => o !== p))
                  }
                />
                <span className="text-sm">
                  <span className="font-medium text-ink">{PAYMENT_LABEL[p].label}</span>
                  <span className="block text-[13px] text-muted">{PAYMENT_LABEL[p].note}</span>
                </span>
              </label>
              {p === "invoice" && draft.paymentOptions.includes("invoice") ? (
                <label className="ml-7 mt-2 block max-w-xs">
                  <span className={LABEL}>Invoice terms</span>
                  <select className={SELECT} value={draft.invoiceTerms} onChange={(e) => set("invoiceTerms", e.target.value as InvoiceTerms)}>
                    {(TERMS_CHOICES.includes(draft.invoiceTerms) ? TERMS_CHOICES : [...TERMS_CHOICES, draft.invoiceTerms]).map((t) => (
                      <option key={t} value={t}>{TERMS_LABEL[t]}</option>
                    ))}
                  </select>
                </label>
              ) : null}
              </div>
            ))}
          </div>
        </Section>
        )}

        <Section title="Where orders ship" intro="Set this before the store goes live.">
          {isNew || store?.shipToListReady !== false ? (
            <ShipToEditor draft={draft} setDraft={setDraft} />
          ) : (
            <p className="text-sm text-muted">Store delivery addresses turn on after a database step from Jake. Until then each person enters their own address.</p>
          )}
        </Section>

        <Section title="End date" intro="The last day employees can order. From the next day the store is closed: they can still look, but not order. Orders already placed can still be approved and paid.">
          {isNew || store?.endDateReady !== false ? (
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-ink"
                  checked={draft.noEndDate}
                  onChange={(e) => setDraft((d) => ({ ...d, noEndDate: e.target.checked }))}
                />
                No end date
              </label>
              {!draft.noEndDate ? (
                <label className="block max-w-[220px]">
                  <span className={LABEL}>Last day to order</span>
                  <Input type="date" value={draft.endsOn} onChange={(e) => set("endsOn", e.target.value)} aria-label="Store end date" />
                </label>
              ) : null}
              {store?.ended ? <p className="text-sm font-semibold text-risk">This store has ended — employees can't place new orders.</p> : null}
            </div>
          ) : (
            <p className="text-sm text-muted">End dates turn on after a database step from Jake. Until then the store has no end date.</p>
          )}
        </Section>

        <Section title="Shipping charge" intro="Optional. Set a shipping charge by order size (the items total, before tax) and employees pay exactly that. With no ranges, shipping is worked out from each person's address.">
          <ShippingRangesEditor
            rows={draft.shippingRanges}
            onChange={(rows) => set("shippingRanges", rows)}
            ready={isNew || store?.shippingRangesReady !== false}
          />
        </Section>

        <Section title="Order approval" intro="Which orders the store admin approves before we make them. The admin can change this later.">
          <div className="space-y-3" role="radiogroup" aria-label="Order approval">
            {([
              ["none", "No approval", "Orders go straight through."],
              ["all", "Every order", "The store admin approves each order."],
              ["over_amount", "Orders over an amount", "Only orders above the amount need the admin's approval."],
            ] as const).map(([value, label, note]) => (
              <label key={value} className="flex items-start gap-3">
                <input
                  type="radio"
                  name="approval-rule"
                  className="mt-0.5 h-4 w-4 accent-ink"
                  checked={draft.approvalRule === value}
                  onChange={() => set("approvalRule", value)}
                />
                <span className="text-sm">
                  <span className="font-medium text-ink">{label}</span>
                  <span className="block text-[13px] text-muted">{note}</span>
                </span>
              </label>
            ))}
            {draft.approvalRule === "over_amount" ? (
              <label className="ml-7 block max-w-[200px]">
                <span className={LABEL}>Orders over ($)</span>
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="1"
                  value={draft.approvalThreshold}
                  onChange={(e) => set("approvalThreshold", e.target.value)}
                  placeholder="250"
                />
              </label>
            ) : null}
          </div>
        </Section>
      </fieldset>

      {canEdit ? (
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          {!isNew && dirty ? <span className="text-[13px] text-muted">You have unsaved changes.</span> : null}
          <Button onClick={onSave} disabled={save.isPending || !dirty}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {save.isPending ? "Saving…" : isNew ? "Create store" : dirty ? "Save changes" : "Saved"}
          </Button>
        </div>
      ) : null}

      {store ? (
        <div className="mt-6 space-y-6">
          <Section title="Client sign-off" intro="The client looks over the store and approves it (and the logo) from one link. Once they approve, the store goes live and they become its admin.">
            <SignoffSection store={store} dirty={dirty} canEdit={canEdit} />
          </Section>
          <Section title="People" intro="Who can order from the store. The store admin adds people once the store is live.">
            <PeopleSection store={store} />
          </Section>
          <Section title="Orders" intro="Every order from this store. Placed orders are sales orders in Flow and on the Production Report.">
            <StoreOrdersSection storeId={store.id} canEdit={canEdit} />
          </Section>
        </div>
      ) : (
        <p className="mt-6 text-sm text-muted">Sign-off, people and orders come next, once the store is created.</p>
      )}
    </div>
  );
}

function Section(props: { title: string; intro?: string; children: ReactNode }) {
  return (
    <section className="rounded-md border border-rule bg-white p-5 sm:p-6" aria-label={props.title}>
      <h2 className="text-base font-semibold text-ink">{props.title}</h2>
      {props.intro ? <p className="mt-1 max-w-2xl text-[13px] text-muted">{props.intro}</p> : null}
      <div className="mt-4">{props.children}</div>
    </section>
  );
}

function Notice(props: { kind: "ok" | "err"; text: string; onClose: () => void }) {
  return (
    <div
      role={props.kind === "err" ? "alert" : "status"}
      className={cn(
        "mb-6 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm",
        props.kind === "err" ? "border-risk/30 bg-risk-bg text-risk" : "border-rule bg-canvas text-ink",
      )}
    >
      <span className="flex items-center gap-2">
        {props.kind === "ok" ? <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden /> : null}
        {props.text}
      </span>
      <button type="button" onClick={props.onClose} aria-label="Dismiss" className="rounded-md p-0.5 text-muted hover:text-ink">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Account search, as on Quick virtual (GET /clients?limit=8&search=). */
function ClientPicker(props: { value: { id: string; name: string } | null; onChange: (c: { id: string; name: string } | null) => void; disabled: boolean }) {
  const [search, setSearch] = useState("");
  const q = useQuery({
    queryKey: ["company-stores", "client-search", search],
    queryFn: () => api<{ rows: Array<{ id: string; name: string }> }>(`/clients?limit=8&search=${encodeURIComponent(search.trim())}`),
    enabled: search.trim().length >= 2,
  });
  if (props.value) {
    return (
      <div className="flex h-10 items-center justify-between rounded-md border border-rule bg-canvas px-3 text-sm">
        <span className="truncate font-medium text-ink">{props.value.name || "Client"}</span>
        {!props.disabled ? (
          <button type="button" onClick={() => props.onChange(null)} aria-label="Change client" className="text-muted hover:text-ink">
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted" aria-hidden />
      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search accounts" aria-label="Search accounts" className="pl-9" />
      {search.trim().length >= 2 ? (
        <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-rule bg-white shadow-lg">
          {(q.data?.rows ?? []).map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={() => { props.onChange({ id: c.id, name: c.name }); setSearch(""); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-cream-100"
              >
                {c.name}
              </button>
            </li>
          ))}
          {q.isLoading ? <li className="px-3 py-2 text-sm text-muted">Searching…</li> : null}
          {q.data && !q.data.rows.length ? <li className="px-3 py-2 text-sm text-muted">No accounts match.</li> : null}
        </ul>
      ) : null}
    </div>
  );
}

function inkWords(l: UploadedLogo): string {
  if (l.fullColour) return "Full color — printed as a full-color transfer.";
  if (l.colours === null) return l.note || "We couldn't count the colors. The art team will check it.";
  return `${l.colours} ink color${l.colours === 1 ? "" : "s"}.`;
}

function LogoSection(props: {
  store: StoreDetail | null;
  logoFileId: string | null;
  uploaded: { logo: UploadedLogo; preview: string | null } | null;
  onUploaded: (logo: UploadedLogo, preview: string | null) => void;
  onUndo: () => void;
  disabled: boolean;
}) {
  const { store, uploaded } = props;
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: (file: File) =>
      api<{ logo: UploadedLogo }>("/storefront/logos", {
        method: "POST",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          // Header values must be plain ASCII.
          "X-Filename": file.name.replace(/[^\x20-\x7e]/g, "_").slice(0, 120),
        },
      }),
    onSuccess: (r, file) => {
      setError(null);
      props.onUploaded(r.logo, file.type.startsWith("image/") ? URL.createObjectURL(file) : null);
    },
    onError: (e) => setError(apiErrorMessage(e, "We couldn't upload that logo. Please try again.")),
  });

  const showingNew = !!uploaded && props.logoFileId === uploaded.logo.id;
  const saved = !showingNew && store?.logo && props.logoFileId === store.logoFileId ? store.logo : null;
  const signedOff = !!store && store.status !== "draft";

  return (
    <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
      <div className="flex h-36 w-36 shrink-0 items-center justify-center overflow-hidden rounded-md border border-rule bg-canvas p-2">
        {showingNew && uploaded.preview ? (
          <img src={uploaded.preview} alt="New logo" className="max-h-full max-w-full object-contain" />
        ) : saved?.url && saved.mimeType.startsWith("image/") ? (
          <img src={saved.url} alt="Store logo" className="max-h-full max-w-full object-contain" />
        ) : showingNew || saved ? (
          <span className="px-2 text-center text-[13px] text-muted">{showingNew ? uploaded.logo.name : saved?.name}</span>
        ) : (
          <span className="text-[13px] text-muted">No logo yet</span>
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2 text-sm">
        {showingNew ? (
          <>
            <p className="font-medium text-ink">{uploaded.logo.name} <span className="font-normal text-muted">(not saved yet)</span></p>
            <p className="text-ink">{inkWords(uploaded.logo)}</p>
            {uploaded.logo.palette.length ? (
              <div className="flex flex-wrap gap-1.5" aria-label="Colors found in the logo">
                {uploaded.logo.palette.map((c) => (
                  <span key={c} title={c} className="h-6 w-6 rounded border border-rule" style={{ backgroundColor: c }} />
                ))}
              </div>
            ) : null}
          </>
        ) : saved ? (
          <p className="text-ink">
            {saved.name}
            {store?.logoApprovedAt ? <span className="block text-[13px] text-muted">Client approved this logo on {shortDate(store.logoApprovedAt)}.</span> : null}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button variant="outline" onClick={() => input.current?.click()} disabled={props.disabled || upload.isPending}>
            {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <ImagePlus className="h-4 w-4" aria-hidden />}
            {upload.isPending ? "Uploading…" : props.logoFileId ? "Replace logo" : "Upload logo"}
          </Button>
          {showingNew && store ? (
            <Button variant="ghost" onClick={props.onUndo} disabled={props.disabled || upload.isPending}>Keep the old logo</Button>
          ) : null}
        </div>
        <input
          ref={input}
          type="file"
          accept=".png,.jpg,.jpeg,.gif,.webp,.svg,.pdf,.ai,.eps,image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/pdf,application/postscript"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) upload.mutate(f);
          }}
        />
        <p className={HELP}>PNG, JPG, SVG, PDF, AI or EPS. We count its ink colors to price the printing.</p>
        <p className={cn(HELP, signedOff && "text-warn")}>
          {signedOff
            ? "This store is already with the client. Saving a new logo sends it back to them to sign off again."
            : "Changing the logo after the client signs off sends the store back to them for sign-off."}
        </p>
        {error ? <p role="alert" className="text-[13px] text-risk">{error}</p> : null}
      </div>
    </div>
  );
}

interface ContactRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  isPrimary: boolean | null;
}

const contactName = (c: ContactRow | undefined) => (c ? `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || c.email || "Contact" : null);

function SignoffSection({ store, dirty, canEdit }: { store: StoreDetail; dirty: boolean; canEdit: boolean }) {
  const qc = useQueryClient();
  const contactsQ = useQuery({
    queryKey: ["clients", store.clientId, "contacts"],
    queryFn: () => api<{ contacts: ContactRow[] }>(`/clients/${store.clientId}/contacts`),
  });
  const contacts = contactsQ.data?.contacts ?? [];
  const [contactId, setContactId] = useState<string>("");
  useEffect(() => {
    if (contactId || !contacts.length) return;
    const pick =
      contacts.find((c) => c.id === store.signoffContactId) ??
      contacts.find((c) => c.isPrimary && c.email) ??
      contacts.find((c) => c.email);
    if (pick) setContactId(pick.id);
  }, [contacts, contactId, store.signoffContactId]);
  const [result, setResult] = useState<{ kind: "ok" | "err"; text: string; url?: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const refresh = (s?: StoreDetail) => {
    if (s) qc.setQueryData(["company-stores", "detail", store.id], { store: s });
    else void qc.invalidateQueries({ queryKey: ["company-stores", "detail", store.id] });
    void qc.invalidateQueries({ queryKey: ["company-stores", "list"] });
  };

  const send = useMutation({
    mutationFn: () =>
      api<{ signoffUrl: string; emailed: boolean }>(`/company-stores/${store.id}/signoff`, {
        method: "POST",
        body: JSON.stringify({ contactId }),
      }),
    onSuccess: (r) => {
      const who = contactName(contacts.find((c) => c.id === contactId)) ?? "the client";
      setResult(
        r.emailed
          ? { kind: "ok", text: `Sent to ${who}. They'll get a link to look over the store and approve it.` }
          : { kind: "err", text: `We couldn't email ${who}. Copy this link and send it to them yourself:`, url: r.signoffUrl },
      );
      refresh();
    },
    onError: (e) => setResult({ kind: "err", text: errText(e, "We couldn't send it. Please try again.") }),
  });

  const pause = useMutation({
    mutationFn: (status: "live" | "paused") =>
      api<{ store: StoreDetail }>(`/company-stores/${store.id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
    onSuccess: (r) => {
      setResult({ kind: "ok", text: r.store.status === "paused" ? "Store paused. People can't order until you re-open it." : "Store re-opened." });
      refresh(r.store);
    },
    onError: (e) => setResult({ kind: "err", text: errText(e, "We couldn't change the store. Please try again.") }),
  });

  // People can't order a product with no website price, so the client
  // mustn't approve a store that has one (UX review 2026-10-12).
  // The go-live checklist (Stephanie 2026-10-22): every line must be ✓ to send.
  const checklist = storeChecklist(store, new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }), { pay: (p) => PAYMENT_LABEL[p].label, terms: (t) => TERMS_LABEL[t] });
  const missing = checklist.filter((c) => !c.ok).map((c) => c.label);
  const sendable = store.status === "draft" || store.status === "awaiting_signoff";
  const sentTo = contactName(contacts.find((c) => c.id === store.signoffContactId));
  const changesRequested = store.status === "draft" && !!store.signoffNote;
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  const link = result?.url ?? store.signoffUrl;

  return (
    <div className="space-y-4 text-sm">
      {changesRequested ? (
        <div role="alert" className="rounded-md border border-warn/40 bg-warn-bg px-4 py-3 text-warn">
          <p className="font-semibold">{sentTo ?? "The client"} asked for changes</p>
          <p className="mt-1 whitespace-pre-wrap text-ink">“{store.signoffNote}”</p>
          <p className="mt-2 text-[13px]">Make the changes, save, then send it again.</p>
        </div>
      ) : null}

      <dl className="flex flex-wrap gap-x-8 gap-y-2">
        <div>
          <dt className="text-xs text-muted">Status</dt>
          <dd className="mt-0.5"><StatusPill tone={STATUS_TONE[store.status]} className="normal-case">{STATUS_LABEL[store.status]}</StatusPill></dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Sent</dt>
          <dd className="mt-0.5 font-medium text-ink">{store.signoffSentAt ? `${shortDate(store.signoffSentAt)}${sentTo ? ` to ${sentTo}` : ""}` : "Not yet"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted">Signed off</dt>
          <dd className="mt-0.5 font-medium text-ink">{store.signedOffAt ? `${shortDate(store.signedOffAt)}${sentTo ? ` by ${sentTo}` : ""}` : "Not yet"}</dd>
        </div>
      </dl>

      {link && store.status === "awaiting_signoff" ? (
        <div>
          <span className={LABEL}>Sign-off link</span>
          <div className="flex max-w-2xl items-center gap-2">
            <Input readOnly value={link} onFocus={(e) => e.target.select()} aria-label="Sign-off link" />
            <Button variant="outline" onClick={() => void copy(link)}>
              <Copy className="h-4 w-4" aria-hidden /> {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      ) : null}

      {result ? (
        <p role={result.kind === "err" ? "alert" : "status"} className={result.kind === "err" ? "text-risk" : "text-ink"}>{result.text}</p>
      ) : null}

      {canEdit && sendable ? (
        <div className="space-y-3 border-t border-rule pt-4">
          <label className="block max-w-md">
            <span className={LABEL}>Who signs off</span>
            {contactsQ.isLoading ? (
              <p className="text-muted">Loading contacts…</p>
            ) : contacts.length === 0 ? (
              <p className="text-muted">
                This client has no contacts yet. Add one on the <Link to={`/clients/${store.clientId}/contacts`} className="text-ink underline underline-offset-4">client's Contacts tab</Link> first.
              </p>
            ) : (
              <select className={SELECT} value={contactId} onChange={(e) => setContactId(e.target.value)}>
                <option value="">Choose a contact</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id} disabled={!c.email}>
                    {contactName(c)}{c.email ? ` (${c.email})` : " (no email)"}
                  </option>
                ))}
              </select>
            )}
          </label>
          <div>
            <span className={LABEL}>Before you send</span>
            <ul className="max-w-2xl divide-y divide-rule rounded-md border border-rule" aria-label="Go-live checklist">
              {checklist.map((c) => (
                <li key={c.label} className="flex items-start gap-3 px-3 py-2">
                  {c.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-ok" aria-label="Done" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-risk" aria-label="Missing" />}
                  <span className="min-w-0">
                    <span className="font-medium text-ink">{c.label}</span>
                    <span className={cn("block text-[13px]", c.ok ? "text-muted" : "font-semibold text-risk")}>{c.detail}</span>
                  </span>
                </li>
              ))}
            </ul>
            {missing.length ? <p className="mt-2 text-warn">Fix the {missing.length === 1 ? "item" : `${missing.length} items`} marked ✗, save, then send.</p> : null}
          </div>
          {dirty && !missing.length ? <p className="text-warn">Save your changes first, so the client sees the latest store.</p> : null}
          <Button onClick={() => { setResult(null); send.mutate(); }} disabled={send.isPending || dirty || !!missing.length || !contactId}>
            {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {send.isPending ? "Sending…" : store.status === "awaiting_signoff" ? "Send again" : "Send for sign-off"}
          </Button>
          {store.status === "awaiting_signoff" ? <p className={HELP}>Sending again makes a new link; the old one stops working.</p> : null}
        </div>
      ) : null}

      {canEdit && (store.status === "live" || store.status === "paused") ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-rule pt-4">
          <Button variant="outline" onClick={() => pause.mutate(store.status === "live" ? "paused" : "live")} disabled={pause.isPending || dirty}>
            {pause.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {store.status === "live" ? "Pause store" : "Re-open store"}
          </Button>
          <span className="text-[13px] text-muted">
            {store.status === "live" ? "Pausing stops new orders until you re-open it." : "The store is paused: nobody can order."}
          </span>
        </div>
      ) : null}
      {/* Delete a test or duplicate store (Stephanie 2026-10-30) — any status;
          refused while it has orders still being worked on. */}
      {canEdit ? (
        <div className="flex flex-wrap items-center gap-3 border-t border-rule pt-4">
          <DeleteEntityAction
            deletePath={`/company-stores/${store.id}`}
            triggerLabel="Delete store"
            allowedRoles={STORE_EDITOR_ROLES}
            confirmTitle="Delete this store?"
            confirmBody={
              <>
                This removes <strong className="font-semibold text-brown-500">{store.name}</strong>. Its link stops working
                and it leaves the store list. Orders already placed stay on their projects.
              </>
            }
            redirectTo="/sales/company-stores"
            flashMessage={`${store.name} deleted.`}
            errorFallback="We couldn't delete the store. Nothing was changed — try again."
            invalidateKeys={[["company-stores"]]}
          />
        </div>
      ) : null}
    </div>
  );
}

function PeopleSection({ store }: { store: StoreDetail }) {
  const hasPeople = store.status === "live" || store.status === "paused" || store.memberCount > 0;
  const q = useQuery({
    queryKey: ["company-stores", "members", store.id],
    queryFn: () => api<{ members: StoreMember[] }>(`/company-stores/${store.id}/members`),
    enabled: hasPeople,
  });
  const joinLink = `https://${PORTAL_HOST}/join/${store.slug}`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="space-y-4 text-sm">
      <div>
        <span className={LABEL}>Join link</span>
        {store.status === "live" ? (
          <>
            <div className="flex max-w-2xl items-center gap-2">
              <Input readOnly value={joinLink} onFocus={(e) => e.target.select()} aria-label="Join link" />
              <Button variant="outline" onClick={() => void copy()}>
                <Copy className="h-4 w-4" aria-hidden /> {copied ? "Copied" : "Copy"}
              </Button>
            </div>
            <p className={HELP}>
              {store.joinDomain
                ? `Anyone with an @${store.joinDomain} email can join with this link.`
                : "Joining with this link is off until the store admin turns on “anyone with our company email can join”."}
            </p>
          </>
        ) : store.status === "paused" ? (
          <p className="text-muted">The store is paused, so nobody can join right now. The link comes back when you re-open it.</p>
        ) : (
          <p className="text-muted">The join link appears once the client approves the store.</p>
        )}
      </div>
      {!hasPeople ? (
        <p className="text-muted">Nobody yet. The person who signs off becomes the store admin and adds the rest.</p>
      ) : q.isLoading ? (
        <p className="flex items-center gap-2 text-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading people…</p>
      ) : q.isError ? (
        <p role="alert" className="text-risk">{errText(q.error, "We couldn't load the people in this store.")}</p>
      ) : !q.data?.members.length ? (
        <p className="text-muted">Nobody yet.</p>
      ) : (
        <div className={TABLE_WRAP}>
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                <th scope="col" className={TH}>Name</th>
                <th scope="col" className={TH}>Email</th>
                <th scope="col" className={TH}>Role</th>
                <th scope="col" className={TH}>Status</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Joined</th>
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {q.data.members.map((m) => (
                <tr key={m.id} className={TROW}>
                  <td className={`${TD} font-medium text-ink`}>{m.name}</td>
                  <td className={TD}>{m.email}</td>
                  <td className={TD}>{m.role === "admin" ? "Store admin" : "Member"}</td>
                  <td className={TD}>
                    <StatusPill tone={m.state === "active" ? "active" : "warn"} className="normal-case">
                      {m.state === "active" ? "Active" : "Invited"}
                    </StatusPill>
                  </td>
                  <td className={`${TD} hidden text-muted sm:table-cell`}>{shortDate(m.joinedAt ?? m.invitedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** The store's shipping charge table (Stephanie 2026-10-22, like commonsku):
 *  "$0 – below $100 → $15", "$100 and up → $0". Each row's end is the next
 *  row's start. */
function ShippingRangesEditor(props: { rows: Array<{ from: string; charge: string }>; onChange: (rows: Array<{ from: string; charge: string }>) => void; ready: boolean }) {
  const { rows, onChange, ready } = props;
  if (!ready) {
    return <p className="text-sm text-muted">Shipping ranges turn on after a database step from Jake. Until then, shipping is worked out from each person's address.</p>;
  }
  const put = (i: number, k: "from" | "charge", v: string) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const add = () => {
    if (rows.length === 0) { onChange([{ from: "0", charge: "" }]); return; }
    const last = Number(rows[rows.length - 1]!.from) || 0;
    onChange([...rows, { from: String(last + 100), charge: "" }]);
  };
  return (
    <div>
      {rows.length ? (
        <table className="w-full max-w-xl text-sm">
          <thead>
            <tr className="text-left text-[12px] font-medium text-muted">
              <th className="w-8" />
              <th className="pb-2 font-medium">Order total (items)</th>
              <th className="pb-2 font-medium">Shipping charge</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const next = rows[i + 1];
              return (
                <tr key={i}>
                  <td className="py-1.5 align-middle">
                    <button type="button" className="text-muted hover:text-risk" aria-label={`Remove range ${i + 1}`} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  </td>
                  <td className="py-1.5 pr-4">
                    <span className="flex items-center gap-2">
                      <span className="text-muted">$</span>
                      <Input className="w-24" type="number" inputMode="decimal" min={0} step="0.01" value={r.from} disabled={i === 0} aria-label={`Range ${i + 1} from`} onChange={(e) => put(i, "from", e.target.value)} />
                      <span className="whitespace-nowrap text-muted">{next ? `to below $${next.from || "…"}` : "and up"}</span>
                    </span>
                  </td>
                  <td className="py-1.5">
                    <span className="flex items-center gap-2">
                      <span className="text-muted">$</span>
                      <Input className="w-24" type="number" inputMode="decimal" min={0} step="0.01" value={r.charge} placeholder="0.00" aria-label={`Range ${i + 1} shipping charge`} onChange={(e) => put(i, "charge", e.target.value)} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-muted">No ranges — shipping is worked out from each person's address.</p>
      )}
      <button type="button" onClick={add} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-ink underline">
        <Plus className="h-4 w-4" aria-hidden /> Add range
      </button>
    </div>
  );
}

/** Where the store's orders ship (Stephanie 2026-10-22, like commonsku's
 *  "Selecting from list" / "typing addresses"). */
function ShipToEditor({ draft, setDraft }: { draft: Draft; setDraft: React.Dispatch<React.SetStateAction<Draft>> }) {
  const setMode = (shipMode: Draft["shipMode"]) =>
    setDraft((d) => ({
      ...d,
      shipMode,
      addresses: shipMode === "list" && d.addresses.length === 0 ? [{ ...EMPTY_ADDRESS }] : d.addresses,
      // One billing method for a store that ships to its own address.
      paymentOptions: shipMode === "list" && (d.paymentOptions.length !== 1 || d.paymentOptions[0] === "employee_card") ? ["invoice"] : d.paymentOptions,
    }));
  const put = (i: number, patch: Partial<DraftAddress>) =>
    setDraft((d) => ({ ...d, addresses: d.addresses.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
  return (
    <div className="space-y-3">
      <div className="space-y-2" role="radiogroup" aria-label="Where orders ship">
        {([
          ["list", "All orders ship to the store's address", "Employees don't type an address — their orders ship to the address below. Add more than one and they pick from the list."],
          ["own", "Each person enters their own address", "Employees type where their order should go."],
        ] as const).map(([value, label, note]) => (
          <label key={value} className="flex items-start gap-3">
            <input type="radio" name="ship-mode" className="mt-0.5 h-4 w-4 accent-ink" checked={draft.shipMode === value} onChange={() => setMode(value)} />
            <span className="text-sm">
              <span className="font-medium text-ink">{label}</span>
              <span className="block text-[13px] text-muted">{note}</span>
            </span>
          </label>
        ))}
      </div>
      {draft.shipMode === "list" ? (
        <div className="ml-7 space-y-4">
          {draft.addresses.map((a, i) => (
            <div key={i} className="rounded-md border border-rule p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-ink">{draft.addresses.length > 1 ? `Address ${i + 1}` : "Ship-to address"}</span>
                {draft.addresses.length > 1 ? (
                  <button type="button" className="text-xs font-semibold text-risk underline" onClick={() => setDraft((d) => ({ ...d, addresses: d.addresses.filter((_, j) => j !== i) }))}>Remove</button>
                ) : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {draft.addresses.length > 1 ? (
                  <label className="block sm:col-span-2"><span className={LABEL}>Name employees see (e.g. Head office)</span><Input value={a.label} onChange={(e) => put(i, { label: e.target.value })} /></label>
                ) : null}
                <label className="block sm:col-span-2"><span className={LABEL}>Company / attention (optional)</span><Input value={a.company} onChange={(e) => put(i, { company: e.target.value })} /></label>
                <label className="block sm:col-span-2"><span className={LABEL}>Street address</span><Input value={a.street1} onChange={(e) => put(i, { street1: e.target.value })} /></label>
                <label className="block sm:col-span-2"><span className={LABEL}>Suite, floor (optional)</span><Input value={a.street2} onChange={(e) => put(i, { street2: e.target.value })} /></label>
                <label className="block"><span className={LABEL}>City</span><Input value={a.city} onChange={(e) => put(i, { city: e.target.value })} /></label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className={LABEL}>State</span><Input value={a.stateCode} maxLength={2} placeholder="FL" onChange={(e) => put(i, { stateCode: e.target.value.toUpperCase() })} /></label>
                  <label className="block"><span className={LABEL}>ZIP</span><Input value={a.postalCode} inputMode="numeric" maxLength={10} onChange={(e) => put(i, { postalCode: e.target.value })} /></label>
                </div>
                <label className="flex items-center gap-2 text-sm text-ink sm:col-span-2">
                  <input type="checkbox" className="h-4 w-4 accent-ink" checked={a.residential} onChange={(e) => put(i, { residential: e.target.checked })} />
                  This is a home address
                </label>
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setDraft((d) => ({ ...d, addresses: [...d.addresses, { ...EMPTY_ADDRESS }] }))} className="inline-flex items-center gap-1 text-sm font-semibold text-ink underline">
            <Plus className="h-4 w-4" aria-hidden /> Add another address
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Billing for a store that ships to its own address (Stephanie 2026-10-22:
 *  the rep sets it once per store): Invoice (on terms) or Company card, and
 *  the contact who's on every order and gets the invoice. */
function StoreBillingEditor({ draft, setDraft }: { draft: Draft; setDraft: React.Dispatch<React.SetStateAction<Draft>> }) {
  const clientId = draft.client?.id ?? null;
  const contactsQ = useQuery({
    queryKey: ["clients", clientId, "contacts"],
    queryFn: () => api<{ contacts: ContactRow[] }>(`/clients/${clientId}/contacts`),
    enabled: !!clientId,
  });
  const contacts = contactsQ.data?.contacts ?? [];
  const method = draft.paymentOptions[0] ?? "invoice";
  return (
    <div className="space-y-4">
      <div className="space-y-2" role="radiogroup" aria-label="Billing method">
        {(["invoice", "company_card"] as const).map((p) => (
          <div key={p}>
            <label className="flex items-start gap-3">
              <input type="radio" name="store-billing" className="mt-0.5 h-4 w-4 accent-ink" checked={method === p} onChange={() => setDraft((d) => ({ ...d, paymentOptions: [p] }))} />
              <span className="text-sm">
                <span className="font-medium text-ink">{PAYMENT_LABEL[p].label}</span>
                <span className="block text-[13px] text-muted">{PAYMENT_LABEL[p].note}</span>
              </span>
            </label>
            {p === "invoice" && method === "invoice" ? (
              <label className="ml-7 mt-2 block max-w-xs">
                <span className={LABEL}>Invoice terms</span>
                <select className={SELECT} value={draft.invoiceTerms} onChange={(e) => setDraft((d) => ({ ...d, invoiceTerms: e.target.value as InvoiceTerms }))}>
                  {(TERMS_CHOICES.includes(draft.invoiceTerms) ? TERMS_CHOICES : [...TERMS_CHOICES, draft.invoiceTerms]).map((t) => (
                    <option key={t} value={t}>{TERMS_LABEL[t]}</option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        ))}
      </div>
      <label className="block max-w-sm">
        <span className={LABEL}>Billing contact (on every order, gets the invoice)</span>
        <select className={SELECT} value={draft.billingContactId} onChange={(e) => setDraft((d) => ({ ...d, billingContactId: e.target.value }))} disabled={!clientId}>
          <option value="">{clientId ? (contactsQ.isLoading ? "Loading…" : "Pick a contact") : "Pick the client first"}</option>
          {contacts.map((c) => (
            <option key={c.id} value={c.id}>{contactName(c)}{c.email ? ` — ${c.email}` : ""}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
